import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Grid,
  Typography,
  Stack,
  Box,
  TextField,
  IconButton,
  Radio,
} from '@mui/material';
import { Controller, useFieldArray, useWatch } from 'react-hook-form';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import Iconify from '../common/iconify.component';
import { deleteQuizQuestionOptionById } from '../../services/quiz.service';
import ConfirmPopup from '../common/confirm-popup.component';
import validationRules, {
  maxLengthValidation,
} from '../../utils/validations.util';
import { QUESTION_TYPES, QUIZ_CONFIG } from '../../config/const.config';

const OptionsList = ({ control, handleActionSuccess, type }) => {
  const {
    fields: optionsFields,
    append: optionsAppend,
    remove: optionsRemove,
    move: optionsMove,
    update: optionsUpdate,
  } = useFieldArray({
    control,
    name: `options`,
  });

  const watchedOptions = useWatch({
    control,
    name: 'options',
  });

  const [openConfirm, setOpenConfirm] = useState(false);
  const selectedOptionRef = useRef({
    option: {},
    index: null,
  });

  const handleDeleteOption = () => {
    if (selectedOptionRef.current.option.optionId) {
      deleteQuizQuestionOptionById(selectedOptionRef.current.option.optionId)
        .then(() => {
          handleActionSuccess('Option deleted successfully.');
          optionsRemove(selectedOptionRef.current.index);
          setOpenConfirm(false);
          selectedOptionRef.current = {
            option: {},
            index: null,
          };
        })
        .catch(() => {
          // Skipped
        });
    } else {
      handleActionSuccess('Option deleted successfully.');
      optionsRemove(selectedOptionRef.current.index);
      setOpenConfirm(false);
      selectedOptionRef.current = {
        option: {},
        index: null,
      };
    }
  };

  const onDragEnd = (result) => {
    if (!result.destination) return;
    optionsMove(result.source.index, result.destination.index);
  };

  useEffect(() => {
    if (optionsFields.length === 0) {
      optionsAppend([
        { optionText: '' },
        { optionText: '' },
        { optionText: '' },
      ]);
    }
  }, []);

  return (
    <>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        width="100%"
        sx={{ pl: 2.3, pt: 2.5 }}
      >
        <Typography variant="h6" sx={{ width: '33%', flexShrink: 0 }}>
          Options
        </Typography>

        <Box>
          <Button
            color="secondary"
            variant="contained"
            size="small"
            onClick={() => optionsAppend({ optionText: '' })}
            disabled={
              optionsFields.length >=
              (type && type === QUESTION_TYPES.MATCH.value
                ? QUIZ_CONFIG.MAX_MATCH_OPTION_COUNT
                : QUIZ_CONFIG.MAX_OPTION_COUNT)
            }
          >
            Add Option
          </Button>
        </Box>
      </Stack>

      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="optionsList" isDropDisabled={false}>
          {(provided) => (
            <div
              {...provided.droppableProps}
              ref={provided.innerRef}
              style={{ width: '100%', marginLeft: 16 }}
            >
              {optionsFields?.map((opt, index) => (
                <Draggable
                  key={`option-${opt.id}`}
                  draggableId={opt.id.toString()}
                  index={index}
                >
                  {(provide) => (
                    <Grid
                      container
                      key={opt.id}
                      mt={1}
                      spacing={1}
                      columnSpacing={2}
                      item
                      xs={12}
                      sm={12}
                      md={12}
                      ref={provide.innerRef}
                      {...provide.draggableProps}
                      {...provide.dragHandleProps}
                    >
                      {type !== QUESTION_TYPES.MATCH.value ? (
                        <Grid
                          item
                          xs={0.5}
                          sm={0.5}
                          md={0.5}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            marginLeft: -1,
                            marginRight: 0.5,
                          }}
                        >
                          <Controller
                            control={control}
                            name={`options[${index}].isTrue`}
                            render={({ field }) => (
                              <Radio
                                {...field}
                                checked={opt.isTrue === 1}
                                onChange={() => {
                                  watchedOptions.forEach((option, i) => {
                                    optionsUpdate(i, {
                                      ...option,
                                      isTrue: i === index ? 1 : 0,
                                    });
                                  });
                                }}
                                value={1}
                                size="medium"
                              />
                            )}
                          />
                        </Grid>
                      ) : null}

                      {type && type === QUESTION_TYPES.MATCH.value ? (
                        <>
                          <Grid
                            item
                            xs={optionsFields.length === 1 ? 5.75 : 5.25}
                            sm={optionsFields.length === 1 ? 5.75 : 5.25}
                            md={optionsFields.length === 1 ? 5.75 : 5.25}
                          >
                            <Controller
                              control={control}
                              id={`options[${index}].optionText`}
                              name={`options[${index}].optionText`}
                              rules={{
                                ...validationRules.REQUIRED,
                                ...maxLengthValidation(500),
                              }}
                              render={({
                                field: { onChange, value },
                                fieldState: { error },
                              }) => (
                                <TextField
                                  label={`Option ${index + 1}`}
                                  type="text"
                                  onChange={onChange}
                                  value={value}
                                  autoComplete="off"
                                  error={!!error}
                                  helperText={error?.message || null}
                                  fullWidth
                                />
                              )}
                            />
                          </Grid>

                          <Grid
                            item
                            xs={0.5}
                            sm={0.5}
                            md={0.5}
                            sx={{
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                            }}
                          >
                            <Iconify
                              icon="bi:arrows"
                              sx={{ fontSize: '20px' }}
                            />
                          </Grid>

                          <Grid
                            item
                            xs={optionsFields.length === 1 ? 5.75 : 5.25}
                            sm={optionsFields.length === 1 ? 5.75 : 5.25}
                            md={optionsFields.length === 1 ? 5.75 : 5.25}
                          >
                            <Controller
                              control={control}
                              id={`options[${index}].mappedOptionText`}
                              name={`options[${index}].mappedOptionText`}
                              rules={{
                                ...validationRules.REQUIRED,
                                ...maxLengthValidation(500),
                              }}
                              render={({
                                field: { onChange, value },
                                fieldState: { error },
                              }) => (
                                <TextField
                                  label={`Match ${index + 1}`}
                                  type="text"
                                  onChange={onChange}
                                  value={value}
                                  autoComplete="off"
                                  error={!!error}
                                  helperText={error?.message || null}
                                  fullWidth
                                />
                              )}
                            />
                          </Grid>
                        </>
                      ) : (
                        <Grid
                          item
                          xs={optionsFields.length === 1 ? 11.5 : 10.5}
                          sm={optionsFields.length === 1 ? 11.5 : 10.5}
                          md={optionsFields.length === 1 ? 11.5 : 10.5}
                        >
                          <Controller
                            control={control}
                            id={`options[${index}].optionText`}
                            name={`options[${index}].optionText`}
                            rules={{
                              ...validationRules.REQUIRED,
                              ...maxLengthValidation(500),
                            }}
                            render={({
                              field: { onChange, value },
                              fieldState: { error },
                            }) => (
                              <TextField
                                label={`Option ${index + 1}`}
                                type="text"
                                onChange={onChange}
                                value={value}
                                autoComplete="off"
                                error={!!error}
                                helperText={error?.message || null}
                                fullWidth
                              />
                            )}
                          />
                        </Grid>
                      )}

                      {optionsFields.length > 1 && (
                        <>
                          <Grid
                            item
                            xs={0.5}
                            sm={0.5}
                            md={0.5}
                            sx={{
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                            }}
                          >
                            <IconButton
                              aria-label="order"
                              size="small"
                              disableRipple
                              sx={{ cursor: '-webkit-grab' }}
                            >
                              <Iconify icon="ic:round-table-rows" />
                            </IconButton>
                          </Grid>

                          <Grid
                            item
                            xs={0.5}
                            sm={0.5}
                            md={0.5}
                            sx={{
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                            }}
                          >
                            <IconButton
                              aria-label="delete"
                              size="small"
                              onClick={() => {
                                selectedOptionRef.current.index = index;
                                selectedOptionRef.current.option = opt;
                                setOpenConfirm(true);
                              }}
                            >
                              <Iconify icon="material-symbols:delete-outline" />
                            </IconButton>
                          </Grid>
                        </>
                      )}
                    </Grid>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      {openConfirm && (
        <ConfirmPopup
          title="Delete Quiz"
          message="Do you want to delete this option? You can not undo this action!"
          onCancel={() => {
            setOpenConfirm(false);
            selectedOptionRef.current = {
              option: {},
              index: null,
            };
          }}
          onSuccess={handleDeleteOption}
        />
      )}
    </>
  );
};

OptionsList.propTypes = {
  control: PropTypes.func.isRequired,
  handleActionSuccess: PropTypes.func.isRequired,
  type: PropTypes.string.isRequired,
};

export default OptionsList;
